import React from 'react';
import {
  shape, arrayOf, func, string,
} from 'prop-types';
import {
  Typography, DownloadIcon, DashboardDetails,
  PhoneNumber, EmailLink, COLORS,
} from '@hyphengroup/cag-components';
import {
  withTracking,
  loginDashboardClaimEmailEvent,
  loginDashboardCallHotlineEvent,
  loginDashboardTermsAndConditionsClickEvent,
  loginDashboardFAQClickEvent,
} from 'components/tracking';
import { getObjectFromArray } from 'pages/Verticals/ResultsPage/ResultsPageUtils';
import { path } from 'ramda';
import moment from 'moment';
import { formatBytes, getDashboardDesktopMenuBar } from 'helpers/Utils';
import withApplicationConfig from 'containers/withApplicationConfig';

const mapStatusToColor = {
  ACTIVE: COLORS.GREEN,
  UPCOMING: COLORS.BKGROUND_04,
  EXPIRED: COLORS.ALERT_02,
};

export const mapPolicyStatus = {
  ACTIVE: 'ACTIVE',
  UPCOMING: 'UPCOMING',
  EXPIRED: 'EXPIRED',
};

export function DashboardMoreInfoOB({
  data, header: { logo }, footer, history, baseURL, showAccountMenu, track,
  obDashboardDetailsFields,
}) {
  const customerProducts = data.customerProfile?.customerProducts?.products;
  const product = customerProducts && customerProducts.length && customerProducts[0];
  if (!product) {
    return null;
  }

  const {
    insured, status, referenceNumber, enrollmentDate, policyStartDate, policyEndDate,
    product: {
      id, productName, policyDocumentation, provider, image,
      claims: { phone: claimsPhone, email: claimsEmail },
    },
  } = product;

  const staticText = getObjectFromArray(path(['pageConfig', 'staticPageText', 'staticText'], data));
  const termsAndConditions = policyDocumentation.find(document => document && document.type.key === 'TERMS_CONDITIONS')?.file;
  const faq = policyDocumentation.find(document => document && document.type.key === 'FAQ')?.file;

  const fileSizes = [
    staticText.FILE_SIZE_BYTES,
    staticText.FILE_SIZE_KB,
    staticText.FILE_SIZE_MB,
    staticText.FILE_SIZE_GB,
  ];

  function getFormatDate(date) {
    return moment(date).format('DD MMM YYYY');
  }

  const detailsData = {
    claimDetails: {
      email: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_CLAIM_EMAIL,
        content: (
          <EmailLink
            variant="bold"
            email={claimsEmail}
            onClick={() => loginDashboardClaimEmailEvent(track, 'OB', product)}
          >
            {claimsEmail}
          </EmailLink>
        ),
        contentMobile: (
          <EmailLink
            variant="bold"
            email={claimsEmail}
            onClick={() => loginDashboardClaimEmailEvent(track, 'OB', product)}
          >
            {staticText.PURCHASE_DETAILS_OB_EMAIL}
          </EmailLink>
        ),
      },
      hotline: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_CLAIM_HOTLINE,
        content: (
          <PhoneNumber
            number={claimsPhone}
            showIcon={false}
            variant="bold"
            onClick={() => loginDashboardCallHotlineEvent(track, 'OB', product)}
          />
        ),
      },
    },
    insuranceInformation: {
      referenceNumber: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_REFERENCE_NUMBER,
        content: referenceNumber,
      },
      registrationDate: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_REGISTRATION_DATE,
        content: getFormatDate(enrollmentDate),
      },
      effectiveDate: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_EFFECTIVE_DATE,
        content: getFormatDate(policyStartDate),
      },
      expiryDate: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_EXPIRY_DATE,
        content: getFormatDate(policyEndDate),
      },
      insuranceStatus: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_INSURANCE_STATUS,
        content: (
          <Typography
            variant="secondary"
            sizeFont="px14"
            weight="bold"
            color={mapStatusToColor[status]}
          >
            {staticText[`PURCHASE_DETAILS_OB_STATUS_${mapPolicyStatus[status]}`]}
          </Typography>
        ),
      },
      fullName: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_INSURED_PERSON_NAME,
        content: `${insured[0].firstName} ${insured[0].familyName}`,
      },
      givenName: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_INSURED_PERSON_GIVEN_NAME,
        content: insured[0].firstName,
      },
      familyName: {
        title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_INSURED_PERSON_FAMILY_NAME,
        content: insured[0].familyName,
      },
    },
    documents: {},
  };

  if (termsAndConditions) {
    detailsData.documents.termsAndConditions = {
      title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_TERMS_AND_CONDITIONS,
      content: (
        <DownloadIcon
          link={termsAndConditions.url}
          label={formatBytes(termsAndConditions.fileSize, fileSizes)}
          onClick={() => {
            loginDashboardTermsAndConditionsClickEvent(track, 'OB', product && {
              id,
              name: productName,
              brand: provider.providerName,
              category: 'online-banking-insurance',
            });
          }
          }
        />
      ),
      alignItems: 'center',
    };
  }

  if (faq) {
    detailsData.documents.faq = {
      title: staticText.PURCHASE_DETAILS_OB_FIELD_LABEL_FAQ,
      content: (
        <DownloadIcon
          link={faq.url}
          label={formatBytes(faq.fileSize, fileSizes)}
          onClick={() => {
            loginDashboardFAQClickEvent(track, 'OB', product && {
              id,
              name: productName,
              brand: provider.providerName,
              category: 'online-banking-insurance',
            });
          }
          }
        />
      ),
      alignItems: 'center',
    };
  }

  const obDetailsData = [
    {
      sections: [
        {
          header: staticText.PURCHASE_DETAILS_OB_TITLE_CLAIM_DETAILS,
          rows: obDashboardDetailsFields.claimDetails?.map(fieldName => (
            detailsData.claimDetails[fieldName])),
        },
        {
          header: staticText.PURCHASE_DETAILS_OB_TITLE_INSURANCE_INFORMATION,
          rows: obDashboardDetailsFields.insuranceInformation?.map(fieldName => (
            detailsData.insuranceInformation[fieldName])),
        },
        {
          header: staticText.PURCHASE_DETAILS_OB_TITLE_TERMS_AND_CONDITIONS,
          rows: obDashboardDetailsFields
            .documents?.filter(fieldName => detailsData.documents[fieldName])
            .map(fieldName => detailsData.documents[fieldName]),
        },
      ],
    },
  ];

  return (
    <DashboardDetails
      data={obDetailsData}
      productTitle={productName}
      productLogo={image.url}
      productLogoStyle="outline"
      onBackClick={() => history.push(`${baseURL}/${staticText.DASHBOARD_ROUTE_NAME}`)}
      backBtnLabel={staticText.BACK}
      desktopMenuBar={getDashboardDesktopMenuBar(staticText)}
      siteLogo={{
        link: logo?.link?.href,
        imageTitle: logo?.title,
        imageUrl: footer?.logo?.image?.url,
      }}
      onRightIconClick={showAccountMenu}
    />
  );
}

DashboardMoreInfoOB.defaultProps = {
  track: null,
};

DashboardMoreInfoOB.propTypes = {
  track: func,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  baseURL: string.isRequired,
  data: shape({
    customerProfile: shape({
      customerProducts: shape({
        products: arrayOf(
          shape({
            insured: shape({
              fullName: string,
            }),
            status: string,
            referenceNumber: string,
            enrollmentDate: string,
            policyStartDate: string,
            policyEndDate: string,
            product: shape({
              productName: string,
              policyDocumentation: shape({
                type: shape({
                  key: string,
                  file: shape({
                    url: string,
                    fileSize: string,
                  }),
                }),
              }),
              image: shape({
                url: string,
              }),
              provider: shape({
                providerImage: shape({
                  url: string,
                }),
              }),
              claims: shape({
                phone: string,
                email: string,
              }),
            }),
          }).isRequired
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  footer: shape({
    logo: shape({
      image: shape({
        url: string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  header: shape({
    logo: shape({
      link: shape({
        href: string.isRequired,
      }).isRequired,
      title: string.isRequired,
    }).isRequired,
  }).isRequired,
  showAccountMenu: func.isRequired,
  obDashboardDetailsFields: shape({
    claimDetails: arrayOf(string),
    insuranceInformation: arrayOf(string),
    documents: arrayOf(string),
  }),
};

export default withTracking(withApplicationConfig(DashboardMoreInfoOB, 'obDashboardDetails'));
